import { Container, Typography, Grid } from "@mui/material";
import dayjs from "dayjs";

import { TProject } from "../../../../models";
import { BurnDownChart, IBurnDownChartProps } from "./burndown-chart";
import { ProgressCircle } from "./progress-circle";
import { ISCurveChartProps, SCurveChart } from "./scurve-chart";

const h4Style = {
    color: "rgba(62, 55, 78, 0.87)",
    fontFamily: "Noto Sans",
    fontSize: "1.5rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "2.0625rem",
};

type TDashboardTab = {
    project: TProject;
};

export const DashboardTab = ({ project }: TDashboardTab) => {
    const { expectedCost, estimatedHours } = project;
    const totalTimeInHours = (project.total_time / 60).toFixed(0);

    const hoursPerDayFromAllMembers = (project.Users?.length || 0) * 8;

    const formatCost = (value: number) => {
        const formattedValue = value.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
        });
        return formattedValue;
    };
    const formatHours = (value: number) => {
        const formattedValue = value.toLocaleString("pt-BR", {
            style: "decimal",
        });
        return formattedValue;
    };

    const calculateIdealHours = () => {
        if (!project.start_at || !project.end_at || !estimatedHours) {
            return 0;
        }

        const startDate = dayjs(project.start_at);
        const endDate = dayjs(project.end_at);
        const today = dayjs();

        const totalDays = endDate.diff(startDate, "day");
        const elapsedDays = Math.min(today.diff(startDate, "day"), totalDays);

        if (totalDays === 0) return 0;

        const idealHours = (elapsedDays / totalDays) * Number(estimatedHours);

        return idealHours > Number(estimatedHours)
            ? Number(estimatedHours)
            : idealHours;
    };

    const calculateHoursProgress = () => {
        const totalHours = Number(estimatedHours);
        const elapsedHours = Number(totalTimeInHours);
        const progress = (elapsedHours / totalHours) * 100;
        return progress > 100 ? 100 : progress;
    };

    const calculateIdealProgress = () => {
        const idealHours = calculateIdealHours();
        const totalHours = Number(estimatedHours);

        if (totalHours === 0) {
            return 0;
        }

        const progress = (idealHours / totalHours) * 100;
        return progress > 100 ? 100 : progress;
    };

    const calculateHoursDeviation = () => {
        const idealHoursProgress = calculateIdealProgress();
        const actualHoursProgress = calculateHoursProgress();
        const deviation = idealHoursProgress - actualHoursProgress;
        return deviation / 100;
    };

    const formatPercentage = (value: number) => {
        const formattedValue = value.toLocaleString("pt-BR", {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return formattedValue;
    };

    const calculateRealizedCost = () => {
        if (!project?.TimeEntries || project?.TimeEntries.length === 0) {
            return 0;
        }

        const realizedCost = project.TimeEntries.reduce((acc, entry) => {
            const entryCost =
                (entry.total_time / 60) * Number(entry.User.hourValue);
            return acc + entryCost;
        }, 0);
        return realizedCost;
    };

    const calculateIdealCost = () => {
        if (!project.start_at || !project.end_at || !estimatedHours) {
            return 0;
        }

        const startDate = dayjs(project.start_at);
        const endDate = dayjs(project.end_at);
        const today = dayjs();

        const totalDays = endDate.diff(startDate, "day");
        const elapsedDays = Math.min(today.diff(startDate, "day"), totalDays);

        if (totalDays <= 0) return 0;

        const proportionElapsed = elapsedDays / totalDays;

        const totalEstimatedCost = Number(expectedCost);

        const idealCost = proportionElapsed * totalEstimatedCost;

        return idealCost > totalEstimatedCost ? totalEstimatedCost : idealCost;
    };

    const calculateIdealCostProgress = () => {
        const idealCost = calculateIdealCost();
        const totalCost = Number(expectedCost);
        const progress = (idealCost / totalCost) * 100;
        return progress > 100 ? 100 : progress;
    };

    const calculateCostProgress = () => {
        const totalCost = Number(expectedCost);
        const elapsedCost = calculateRealizedCost();
        const progress = (elapsedCost / totalCost) * 100;
        return progress > 100 ? 100 : progress;
    };

    const calculateCostDeviation = () => {
        const idealCostProgress = calculateIdealCostProgress();
        const actualCostProgress =
            (calculateRealizedCost() / Number(expectedCost)) * 100;
        const deviation = idealCostProgress - actualCostProgress;
        return deviation / 100;
    };

    const buildBurnDownData = (): IBurnDownChartProps["data"] => {
        if (!project.start_at || !project.end_at || !estimatedHours) {
            return [];
        }

        const startDate = dayjs(project.start_at);
        const endDate = dayjs(project.end_at);
        const totalDays = endDate.diff(startDate, "day");
        const totalWeeks = Math.ceil(totalDays / 7);

        const totalEstimatedHours = Number(estimatedHours);

        const timeEntries = project.TimeEntries ?? [];

        const data: IBurnDownChartProps["data"] = [];

        for (let week = 0; week <= totalWeeks; week += 2) {
            const currentDate = startDate.add(week * 7, "day");
            const name = `Semana ${week}`;

            const idealElapsedDays = Math.min(
                currentDate.diff(startDate, "day"),
                totalDays
            );
            const idealProgressHours =
                (idealElapsedDays / totalDays) * totalEstimatedHours;
            const idealRemainingHours = Math.max(
                0,
                totalEstimatedHours - idealProgressHours
            );

            const elapsedHours = timeEntries.reduce((acc, entry) => {
                const entryDate = dayjs(entry.start_at);
                if (
                    entryDate.isBefore(currentDate) ||
                    entryDate.isSame(currentDate)
                ) {
                    return acc + (entry.total_time ?? 0) / 60;
                }
                return acc;
            }, 0);

            const actualRemainingHours = Math.max(
                0,
                totalEstimatedHours - elapsedHours
            );

            data.push({
                name,
                ideal: Number(idealRemainingHours.toFixed(2)),
                actual: Number(actualRemainingHours.toFixed(2)),
            });
        }

        return data;
    };

    const buildSCurveData = (): ISCurveChartProps["data"] => {
        if (
            !project.start_at ||
            !project.end_at ||
            !expectedCost ||
            !estimatedHours
        ) {
            return [];
        }

        const startDate = dayjs(project.start_at);
        const endDate = dayjs(project.end_at);
        const totalDays = endDate.diff(startDate, "day");
        const totalWeeks = Math.ceil(totalDays / 7);

        const totalEstimatedCost = Number(expectedCost);
        const hourCost = totalEstimatedCost / Number(estimatedHours);

        const timeEntries = project.TimeEntries ?? [];

        const data: ISCurveChartProps["data"] = [];

        for (let week = 0; week <= totalWeeks; week += 2) {
            const currentDate = startDate.add(week * 7, "day");
            const name = `Semana ${week}`;

            const idealElapsedDays = Math.min(
                currentDate.diff(startDate, "day"),
                totalDays
            );

            const idealHours = idealElapsedDays * hoursPerDayFromAllMembers;
            const idealCost = Math.min(
                idealHours * hourCost,
                totalEstimatedCost
            );

            const realizedCost = timeEntries.reduce((acc, entry) => {
                const entryDate = dayjs(entry.start_at);
                if (
                    entryDate.isBefore(currentDate) ||
                    entryDate.isSame(currentDate)
                ) {
                    const entryHours = (entry.total_time ?? 0) / 60;
                    return acc + entryHours * Number(entry.User.hourValue);
                }
                return acc;
            }, 0);

            data.push({
                name,
                planned: Number(idealCost.toFixed(2)),
                actual: Number(realizedCost.toFixed(2)),
            });
        }

        return data;
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Typography variant="h5" fontWeight="medium" sx={h4Style}>
                {project.name}
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ProgressCircle
                        title="Saldo de Horas"
                        subtitle="Horas em dia"
                        progress={calculateHoursProgress()}
                        idealProgress={calculateIdealProgress()}
                        description={`Com base no progresso atual, o saldo de horas está em conformidade com as expectativas, com um desvio de ${formatPercentage(
                            calculateHoursDeviation() || 0
                        )}`}
                        planned={formatHours(Number(estimatedHours) ?? 0)}
                        ideal={formatHours(calculateIdealHours() ?? 0)}
                        realized={formatHours(Number(totalTimeInHours) ?? 0)}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <ProgressCircle
                        title="Custo Operacional"
                        subtitle="Custo em dia"
                        progress={calculateCostProgress()}
                        idealProgress={calculateIdealCostProgress()}
                        description={`Com base no progresso atual, o custo está em conformidade com as expectativas, com um desvio de ${formatPercentage(
                            calculateCostDeviation() || 0
                        )}`}
                        planned={formatCost(Number(expectedCost) ?? 0)}
                        ideal={formatCost(calculateIdealCost() ?? 0)}
                        realized={formatCost(calculateRealizedCost() ?? 0)}
                        currency={true}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <BurnDownChart data={buildBurnDownData()} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SCurveChart data={buildSCurveData()} />
                </Grid>
            </Grid>
        </Container>
    );
};
